import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "styled-components";

import { useLazyGetDimensionsQuery } from "api/dimensionApi.slice";
import { ExpandableContent, InfinityLoader, SectionTitleUnderlined } from "common/components";
import { createScrollbarStyle } from "common/styles";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { DimensionItem } from "domains/createMapDetail/components/keyIssueList/DimensionItem";
import {
  getCurrentMapDimensionIds,
  getExistingDimensionIds,
  getLocalImage,
  getNewCustomDimensions,
} from "domains/createMapDetail/selectors";
import type { Dimension } from "domains/dimension/dimension.types";
import { useDimensions } from "domains/dimension/useDimensions";

const TopicList = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding-right: 10px;

  ${createScrollbarStyle()}
`;

const StyledTitle = styled(SectionTitleUnderlined)`
  margin-top: 1rem;
`;

type DimensionsListProps = { className?: string; isLight?: boolean; onEditKeyIssueSelect?: (id: string) => void };

export const DimensionsList = ({ onEditKeyIssueSelect, className }: DimensionsListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fetchDimensions, dimensionsResult] = useLazyGetDimensionsQuery();
  const search = useSelector((state) => state.dimension.search) ?? "";
  const issueIdsOnMap = useSelector(getCurrentMapDimensionIds);
  const existingDimensionIds = useSelector(getExistingDimensionIds);
  const localImage = useSelector(getLocalImage);
  const newCustomDimensions = useSelector(getNewCustomDimensions);
  const [results, setResults] = useState<Dimension[]>([]);
  const translate = useTranslate();

  const existingDimensions = useDimensions(existingDimensionIds);
  const [args, setArgs] = useState<{ offset: number; search: string }>({ offset: 0, search });

  // NOTE: Short term solution to fixing duplicates that are returned from the BE
  // Long term solution is in this ticket: https://www.notion.so/The-search-on-Discover-page-shows-more-results-in-the-UI-than-it-should-1c2cf2e17a4e80528ec5e14acd860ad8?source=copy_link
  const resultsWithoutDuplicates = useMemo(
    () =>
      results.reduce((acc: Dimension[], currentDimension) => {
        const existingDimension = acc.find((item) => item.id === currentDimension.id);

        if (!existingDimension) return acc.concat([currentDimension]);

        return acc;
      }, []),
    [results],
  );

  const customDimensions = [
    ...existingDimensions.map(({ dimension }) => dimension).filter((dimension) => dimension.is_custom_dimension),
    ...newCustomDimensions,
  ];

  const searchForResults = (args: { offset: number; search: string }) => {
    setArgs(args);
    scrollToTop();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchForResults = useCallback(debounce(searchForResults, 800), [searchForResults]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetArgsWithLeading = useCallback(debounce(setArgs, 800, { leading: true, trailing: false }), [
    setArgs,
  ]);

  const scrollToTop = () => {
    containerRef.current?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    void fetchDimensions({ limit: 20, offset: args.offset, search: args.search }, true).then((result) => {
      if (!result.isSuccess) return;
      if (args.offset === 0) setResults(result.data.dimensions);
      else setResults((value) => [...value, ...result.data.dimensions]);
    });
  }, [args.offset, args.search, fetchDimensions]);

  useEffect(() => {
    if (search !== args.search) debouncedSearchForResults({ offset: 0, search });
  }, [args.search, debouncedSearchForResults, search]);

  return (
    <TopicList ref={containerRef} className={className}>
      <ExpandableContent isActive={customDimensions.length !== 0}>
        <StyledTitle>{translate("app.create.my_key_issues")}</StyledTitle>
        {customDimensions.map((dimension) => (
          <DimensionItem
            description={dimension.description}
            id={dimension.id}
            image_url={dimension.image_url ?? localImage ?? null}
            is_custom_dimension={dimension.is_custom_dimension}
            isOnMap={issueIdsOnMap.some((issueId) => issueId === dimension.id)}
            key={dimension.id}
            name={dimension.name}
            onEditSelect={onEditKeyIssueSelect}
            parent_topic_id={dimension.parent_topic_id}
          />
        ))}
      </ExpandableContent>

      {customDimensions.length !== 0 && <StyledTitle>{translate("app.create.all_key_issues")}</StyledTitle>}
      <InfinityLoader
        hasNextPage={dimensionsResult.data === undefined || dimensionsResult.data.dimensions.length >= 20}
        isPending={dimensionsResult.isFetching}
        loadNextPage={() => debouncedSetArgsWithLeading((value) => ({ ...value, offset: value.offset + 20 }))}
      >
        {resultsWithoutDuplicates.map((topic) => (
          <DimensionItem
            {...topic}
            dataTestId={`dimension-list-item-${topic.id}-add-button`}
            isOnMap={issueIdsOnMap.some((id) => id === topic.id)}
            key={topic.id}
          />
        ))}
      </InfinityLoader>
    </TopicList>
  );
};
